import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { MapPin, Phone, Mail, Globe, ArrowUpRight } from 'lucide-react';

const contactItems = [
  {
    icon: MapPin,
    label: 'LOCATION',
    value: 'Ernakulam, Kerala, India',
    href: null,
  },
  {
    icon: Phone,
    label: 'PHONE',
    value: '+91 7403114596',
    href: 'tel:+917403114596',
  },
  {
    icon: Mail,
    label: 'EMAIL',
    value: 'core@turinga.dev',
    href: 'mailto:core@turinga.dev',
  },
  {
    icon: Globe,
    label: 'PORTFOLIO',
    value: 'syamjithas.com',
    href: 'https://syamjithas.com',
  },
];

export const FooterSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });

  return (
    <footer
      id="contact"
      ref={sectionRef}
      data-testid="contact-section"
      className="relative py-24 md:py-36 bg-ai-bg"
    >
      {/* Grid background */}
      <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: `
          linear-gradient(rgba(0,240,255,0.3) 1px, transparent 1px),
          linear-gradient(90deg, rgba(0,240,255,0.3) 1px, transparent 1px)
        `,
        backgroundSize: '80px 80px',
      }} />

      <div className="relative z-10 max-w-5xl mx-auto px-6">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="mb-16"
        >
          <span className="text-ai-text/50 text-xs uppercase tracking-[0.4em] block mb-4">{'// 04'}</span>
          <h2 className="text-2xl md:text-4xl leading-tight mb-4">
            <span className="italic text-human-text font-light">Ready to</span>
            {' '}
            <span className="font-bold uppercase text-ai-primary tracking-wider">BUILD</span>
            {' '}
            <span className="italic text-human-text font-light">the Future?</span>
          </h2>
          <p className="text-ai-text/60 text-base max-w-lg font-light italic">
            Let's discuss how the hybrid model can accelerate your next project.
          </p>
        </motion.div>

        {/* Terminal-style contact */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="border border-ai-secondary/20 bg-ai-bg/60 backdrop-blur-sm max-w-2xl"
        >
          {/* Terminal header */}
          <div className="flex items-center gap-2 px-4 py-2 border-b border-ai-secondary/15">
            <div className="w-2 h-2 rounded-full bg-human-primary/60" />
            <div className="w-2 h-2 rounded-full bg-human-secondary/40" />
            <div className="w-2 h-2 rounded-full bg-order/40" />
            <span className="text-ai-text/50 text-xs ml-2 uppercase tracking-widest">contact.json</span>
          </div>

          {/* Contact entries */}
          <div className="p-5 md:p-6">
            <div className="text-xs text-muted mb-3">
              <span className="text-ai-primary">{'{'}</span>
            </div>

            {contactItems.map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.label}
                  data-testid={`contact-${item.label.toLowerCase()}`}
                  initial={{ opacity: 0, x: -10 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.3 + i * 0.1, duration: 0.5 }}
                  className="flex items-start gap-3 py-3 group"
                >
                  <Icon size={16} className="text-ai-primary/60 mt-0.5 shrink-0" strokeWidth={1.5} />
                  <div className="flex-1 min-w-0">
                    <span className="text-xs text-ai-text/50 uppercase tracking-[0.3em] block mb-1">{`"${item.label}":`}</span>
                    {item.href ? (
                      <a
                        href={item.href}
                        target={item.href.startsWith('http') ? '_blank' : undefined}
                        rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                        className="text-human-text text-base font-light italic hover:text-human-primary transition-colors duration-200 inline-flex items-center gap-1"
                      >
                        {item.value}
                        <ArrowUpRight size={10} className="opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                      </a>
                    ) : (
                      <span className="text-human-text text-base font-light italic">{item.value}</span>
                    )}
                  </div>
                </motion.div>
              );
            })}

            <div className="text-xs text-muted mt-3">
              <span className="text-ai-primary">{'}'}</span>
            </div>
          </div>
        </motion.div>

        {/* Bottom bar */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 0.8, duration: 0.6 }}
          className="mt-16 pt-8 border-t border-ai-secondary/15 flex flex-col md:flex-row items-start md:items-center justify-between gap-4"
        >
          <div className="flex items-center gap-3">
            <img src="/logo.svg" alt="Turinga Dev" className="w-6 h-6" />
            <span className="text-human-primary font-light italic text-sm">turinga</span>
            <span className="text-ai-primary font-semibold text-sm uppercase tracking-widest">.dev</span>
          </div>
          <p className="text-xs text-ai-text/50 uppercase tracking-[0.2em]">
            Analog Wisdom. Digital Velocity. &copy; {new Date().getFullYear()}
          </p>
        </motion.div>
      </div>
    </footer>
  );
};
